import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Badge,
  Card,
  CardBody,
  Row,
  Col,
  Spinner,
  Input,
  FormGroup,
  Label,
} from "reactstrap";
import { getBroadcastsByUsername, expireBroadcast } from "./reportedBroadcastApi";
import UserInfoModal from "../../../components/Common/UserInfoModal";

const UserBroadcastsModal = ({ isOpen, toggle, userId, username }) => {
  const [broadcasts, setBroadcasts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  
  // Filter states
  const [broadcastId, setBroadcastId] = useState("");
  const [status, setStatus] = useState("");
  const [dateRange, setDateRange] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isReported, setIsReported] = useState("");
  const [titleKeyword, setTitleKeyword] = useState("");
  const [descriptionKeyword, setDescriptionKeyword] = useState("");
  
  // Debounced search values
  const [debouncedTitleKeyword, setDebouncedTitleKeyword] = useState("");
  const [debouncedDescriptionKeyword, setDebouncedDescriptionKeyword] = useState("");
  const [debouncedBroadcastId, setDebouncedBroadcastId] = useState("");
  
  // User info modal state
  const [userInfoModal, setUserInfoModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  
  // Expire broadcast modal state
  const [expireModal, setExpireModal] = useState(false);
  const [selectedBroadcast, setSelectedBroadcast] = useState(null);
  const [adminExpirationText, setAdminExpirationText] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  
  const abortControllerRef = useRef(null);
  const isMountedRef = useRef(true);
  const titleKeywordTimeoutRef = useRef(null);
  const descriptionKeywordTimeoutRef = useRef(null);
  const broadcastIdTimeoutRef = useRef(null);

  const limit = 50;

  const fetchBroadcasts = useCallback(async (page = 1, append = false) => {
    if (!userId) return;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setLoading(true);
    try {
      const filters = {
        userId,
        limit,
        page,
        broadcast_id: debouncedBroadcastId || undefined,
        status: status || undefined,
        dateRange: dateRange && !startDate && !endDate ? dateRange : undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        isReported: isReported !== "" ? isReported : undefined,
        titleKeyword: debouncedTitleKeyword || undefined,
        descriptionKeyword: debouncedDescriptionKeyword || undefined,
      };

      const result = await getBroadcastsByUsername(filters);
      
      if (abortController.signal.aborted || !isMountedRef.current) {
        return;
      }

      if (append) {
        setBroadcasts((prev) => [...prev, ...result.broadcasts]);
      } else {
        setBroadcasts(result.broadcasts);
      }

      setCurrentPage(result.pagination.currentPage);
      setTotalPages(result.pagination.totalPages);
      setHasMore(result.pagination.currentPage < result.pagination.totalPages);
    } catch (error) {
      if (error.name !== 'AbortError' && isMountedRef.current) {
        console.error("Error fetching broadcasts:", error);
      }
    } finally {
      if (isMountedRef.current && !abortController.signal.aborted) {
        setLoading(false);
      }
      if (abortControllerRef.current === abortController) {
        abortControllerRef.current = null;
      }
    }
  }, [userId, limit, debouncedBroadcastId, status, dateRange, startDate, endDate, isReported, debouncedTitleKeyword, debouncedDescriptionKeyword]);

  useEffect(() => {
    isMountedRef.current = true;
    if (isOpen && userId) {
      setCurrentPage(1);
      setBroadcasts([]);
      fetchBroadcasts(1, false);
    }

    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (titleKeywordTimeoutRef.current) {
        clearTimeout(titleKeywordTimeoutRef.current);
      }
      if (descriptionKeywordTimeoutRef.current) {
        clearTimeout(descriptionKeywordTimeoutRef.current);
      }
      if (broadcastIdTimeoutRef.current) {
        clearTimeout(broadcastIdTimeoutRef.current);
      }
    };
  }, [isOpen, userId, fetchBroadcasts]);

  // Debounce title keyword
  useEffect(() => {
    if (titleKeywordTimeoutRef.current) {
      clearTimeout(titleKeywordTimeoutRef.current);
    }

    titleKeywordTimeoutRef.current = setTimeout(() => {
      setDebouncedTitleKeyword(titleKeyword);
    }, 1000);

    return () => {
      if (titleKeywordTimeoutRef.current) {
        clearTimeout(titleKeywordTimeoutRef.current);
      }
    };
  }, [titleKeyword]);

  // Debounce description keyword
  useEffect(() => {
    if (descriptionKeywordTimeoutRef.current) {
      clearTimeout(descriptionKeywordTimeoutRef.current);
    }

    descriptionKeywordTimeoutRef.current = setTimeout(() => {
      setDebouncedDescriptionKeyword(descriptionKeyword);
    }, 1000);

    return () => {
      if (descriptionKeywordTimeoutRef.current) {
        clearTimeout(descriptionKeywordTimeoutRef.current);
      }
    };
  }, [descriptionKeyword]);

  // Debounce broadcast ID
  useEffect(() => {
    if (broadcastIdTimeoutRef.current) {
      clearTimeout(broadcastIdTimeoutRef.current);
    }

    broadcastIdTimeoutRef.current = setTimeout(() => {
      setDebouncedBroadcastId(broadcastId);
    }, 1000);

    return () => {
      if (broadcastIdTimeoutRef.current) {
        clearTimeout(broadcastIdTimeoutRef.current);
      }
    };
  }, [broadcastId]);

  // Fetch when debounced values change
  useEffect(() => {
    if (isOpen && userId) {
      setCurrentPage(1);
      fetchBroadcasts(1, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedTitleKeyword, debouncedDescriptionKeyword, debouncedBroadcastId]);

  // Refetch when filters change
  useEffect(() => {
    if (isOpen && userId) {
      setCurrentPage(1);
      fetchBroadcasts(1, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, dateRange, startDate, endDate, isReported]);

  const handleLoadMore = () => {
    if (hasMore && !loading) {
      fetchBroadcasts(currentPage + 1, true);
    }
  };

  const handleViewUserInfo = useCallback((userId) => {
    if (!userId) return;
    setSelectedUserId(userId);
    setUserInfoModal(true);
  }, []);

  const handleExpireBroadcast = useCallback((broadcast) => {
    setSelectedBroadcast(broadcast);
    setExpireModal(true);
  }, []);

  const handleExpireConfirm = async () => {
    if (!adminExpirationText.trim()) {
      alert("Please provide an expiration reason");
      return;
    }

    if (!selectedBroadcast || !selectedBroadcast.broadcast_id) {
      alert("Broadcast ID is missing");
      return;
    }

    setActionLoading(true);
    try {
      await expireBroadcast({
        broadcast_id: selectedBroadcast.broadcast_id,
        adminExpirationText: adminExpirationText.trim(),
      });

      // Refresh broadcasts
      await fetchBroadcasts(currentPage, false);
      setExpireModal(false);
      setAdminExpirationText("");
      setSelectedBroadcast(null);
    } catch (error) {
      console.error("Error expiring broadcast:", error);
      alert(error.response?.data?.message || error.message || "Failed to expire broadcast");
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (broadcastStatus) => {
    switch (broadcastStatus?.toLowerCase()) {
      case "active":
        return <Badge color="success" className="px-2 py-1">Active</Badge>;
      case "expired":
        return <Badge color="secondary" className="px-2 py-1">Expired</Badge>;
      case "pending":
        return <Badge color="warning" className="px-2 py-1">Pending</Badge>;
      case "rejected":
        return <Badge color="danger" className="px-2 py-1">Rejected</Badge>;
      default:
        return <Badge color="info" className="px-2 py-1">{broadcastStatus || "Unknown"}</Badge>;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const handleFilterChange = useCallback((filterType, value) => {
    setCurrentPage(1);
    switch (filterType) {
      case "broadcastId":
        setBroadcastId(value);
        break;
      case "status":
        setStatus(value);
        break;
      case "dateRange":
        setDateRange(value);
        if (value !== "custom") {
          setStartDate("");
          setEndDate("");
        }
        break;
      case "startDate":
        setStartDate(value);
        if (value) setDateRange("custom");
        break;
      case "endDate":
        setEndDate(value);
        if (value) setDateRange("custom");
        break;
      case "isReported":
        setIsReported(value);
        break;
      case "titleKeyword":
        setTitleKeyword(value);
        break;
      case "descriptionKeyword":
        setDescriptionKeyword(value);
        break;
      default:
        break;
    }
  }, []);

  const clearFilters = () => {
    setBroadcastId("");
    setStatus("");
    setDateRange("");
    setStartDate("");
    setEndDate("");
    setIsReported("");
    setTitleKeyword("");
    setDescriptionKeyword("");
    setDebouncedBroadcastId("");
    setDebouncedTitleKeyword("");
    setDebouncedDescriptionKeyword("");
    setCurrentPage(1);
  };

  const handleClose = () => {
    setBroadcasts([]);
    setCurrentPage(1);
    setBroadcastId("");
    setStatus("");
    setDateRange("");
    setStartDate("");
    setEndDate("");
    setIsReported("");
    setTitleKeyword("");
    setDescriptionKeyword("");
    setDebouncedBroadcastId("");
    setDebouncedTitleKeyword("");
    setDebouncedDescriptionKeyword("");
    setUserInfoModal(false);
    setSelectedUserId(null);
    setExpireModal(false);
    setSelectedBroadcast(null);
    setAdminExpirationText("");
    toggle();
  };

  return (
    <>
      <Modal isOpen={isOpen} toggle={handleClose} centered size="xl" className="user-broadcasts-modal">
        <ModalHeader className="bg-primary text-white" toggle={handleClose}>
          <div className="d-flex align-items-center">
            <i className="mdi mdi-broadcast me-2"></i>
            <span>Broadcasts for {username || `User ${userId}`}</span>
          </div>
        </ModalHeader>
        <ModalBody style={{ maxHeight: "70vh", overflowY: "auto" }} className="p-3">
          {/* Filters */}
          <Card className="mb-3 shadow-sm">
            <CardBody className="p-3">
              <Row className="g-3">
                <Col md={4}>
                  <FormGroup className="mb-0">
                    <Label className="small mb-1">Broadcast ID</Label>
                    <Input
                      type="text"
                      placeholder="Search by broadcast ID..."
                      value={broadcastId}
                      onChange={(e) => handleFilterChange("broadcastId", e.target.value)}
                      size="sm"
                    />
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup className="mb-0">
                    <Label className="small mb-1">Title Keyword</Label>
                    <Input
                      type="text"
                      placeholder="Search by title..."
                      value={titleKeyword}
                      onChange={(e) => handleFilterChange("titleKeyword", e.target.value)}
                      size="sm"
                    />
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup className="mb-0">
                    <Label className="small mb-1">Description Keyword</Label>
                    <Input
                      type="text"
                      placeholder="Search by description..."
                      value={descriptionKeyword}
                      onChange={(e) => handleFilterChange("descriptionKeyword", e.target.value)}
                      size="sm"
                    />
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup className="mb-0">
                    <Label className="small mb-1">Status</Label>
                    <Input
                      type="select"
                      value={status}
                      onChange={(e) => handleFilterChange("status", e.target.value)}
                      size="sm"
                    >
                      <option value="">All</option>
                      <option value="active">Active</option>
                      <option value="expired">Expired</option>
                      <option value="pending">Pending</option>
                      <option value="rejected">Rejected</option>
                    </Input>
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup className="mb-0">
                    <Label className="small mb-1">Is Reported</Label>
                    <Input
                      type="select"
                      value={isReported}
                      onChange={(e) => handleFilterChange("isReported", e.target.value)}
                      size="sm"
                    >
                      <option value="">All</option>
                      <option value="true">Reported</option>
                      <option value="false">Not Reported</option>
                    </Input>
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup className="mb-0">
                    <Label className="small mb-1">Date Range</Label>
                    <Input
                      type="select"
                      value={dateRange}
                      onChange={(e) => handleFilterChange("dateRange", e.target.value)}
                      size="sm"
                    >
                      <option value="">None</option>
                      <option value="last_24_hours">Last 24 Hours</option>
                      <option value="last_7_days">Last 7 Days</option>
                      <option value="last_30_days">Last 30 Days</option>
                      <option value="custom">Custom Range</option>
                    </Input>
                  </FormGroup>
                </Col>
                {(dateRange === "custom" || (startDate || endDate)) && (
                  <>
                    <Col md={3}>
                      <FormGroup className="mb-0">
                        <Label className="small mb-1">Start Date</Label>
                        <Input
                          type="date"
                          value={startDate}
                          onChange={(e) => handleFilterChange("startDate", e.target.value)}
                          size="sm"
                        />
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <FormGroup className="mb-0">
                        <Label className="small mb-1">End Date</Label>
                        <Input
                          type="date"
                          value={endDate}
                          onChange={(e) => handleFilterChange("endDate", e.target.value)}
                          size="sm"
                        />
                      </FormGroup>
                    </Col>
                  </>
                )}
                <Col md={12}>
                  <Button color="secondary" size="sm" onClick={clearFilters}>
                    Clear Filters
                  </Button>
                </Col>
              </Row>
            </CardBody>
          </Card>

          {loading && broadcasts.length === 0 ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
              <p className="mt-3 text-muted">Loading broadcasts...</p>
            </div>
          ) : broadcasts.length === 0 ? (
            <div className="text-center py-5">
              <i className="mdi mdi-broadcast-off" style={{ fontSize: "3rem", color: "#6c757d" }}></i>
              <p className="mt-3 text-muted">No broadcasts found for this user.</p>
            </div>
          ) : (
            <>
              {broadcasts.map((broadcast, index) => (
                <Card key={broadcast.broadcast_id || index} className="mb-3 shadow-sm">
                  <CardBody className="p-3">
                    <Row className="g-3">
                      {broadcast.broadcastImageUrl && (
                        <Col md={3}>
                          <img
                            src={broadcast.broadcastImageUrl}
                            alt={broadcast.title}
                            className="img-fluid rounded"
                            style={{ maxHeight: "150px", width: "100%", objectFit: "cover" }}
                            onError={(e) => {
                              e.target.style.display = "none";
                            }}
                          />
                        </Col>
                      )}
                      <Col md={broadcast.broadcastImageUrl ? 9 : 12}>
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <div>
                            <h6 className="mb-1 fw-bold">{broadcast.title || "No Title"}</h6>
                            <div className="d-flex gap-2 mb-2">
                              {getStatusBadge(broadcast.status)}
                              {broadcast.Interest_count !== undefined && (
                                <Badge color="info" className="px-2 py-1">
                                  <i className="mdi mdi-heart me-1"></i>
                                  {broadcast.Interest_count} Interests
                                </Badge>
                              )}
                              {broadcast.reported_count !== undefined && broadcast.reported_count !== null && broadcast.reported_count > 0 && (
                                <Badge color="danger" className="px-2 py-1">
                                  <i className="mdi mdi-flag me-1"></i>
                                  {broadcast.reported_count} Report{broadcast.reported_count !== 1 ? 's' : ''}
                                </Badge>
                              )}
                            </div>
                          </div>
                          {broadcast.status?.toLowerCase() === "active" && (
                            <Button
                              color="warning"
                              size="sm"
                              onClick={() => handleExpireBroadcast(broadcast)}
                              className="px-3"
                            >
                              <i className="mdi mdi-clock-alert me-1"></i>Expire
                            </Button>
                          )}
                        </div>
                        
                        {broadcast.description && (
                          <div className="mb-2">
                            <p className="text-muted small mb-0" style={{ whiteSpace: "pre-wrap" }}>
                              {broadcast.description}
                            </p>
                          </div>
                        )}

                        <Row className="g-2 mt-2">
                          <Col md={6}>
                            <div className="mb-2">
                              <label className="form-label text-muted small mb-1">
                                <i className="mdi mdi-calendar-plus me-1"></i>Created At
                              </label>
                              <div className="small">{formatDate(broadcast.created_at)}</div>
                            </div>
                          </Col>
                          <Col md={6}>
                            <div className="mb-2">
                              <label className="form-label text-muted small mb-1">
                                <i className="mdi mdi-calendar-clock me-1"></i>Expires At
                              </label>
                              <div className="small">{formatDate(broadcast.expiration_time)}</div>
                            </div>
                          </Col>
                          {broadcast.broadcaster && (
                            <Col md={6}>
                              <div className="mb-2">
                                <label className="form-label text-muted small mb-1">
                                  <i className="mdi mdi-account me-1"></i>Broadcaster
                                </label>
                                <div className="d-flex align-items-center gap-2">
                                  {broadcast.broadcaster.primaryImgURL && (
                                    <img
                                      src={broadcast.broadcaster.primaryImgURL}
                                      alt={broadcast.broadcaster.username}
                                      className="rounded-circle"
                                      style={{ width: "30px", height: "30px", objectFit: "cover" }}
                                      onError={(e) => {
                                        e.target.style.display = "none";
                                      }}
                                    />
                                  )}
                                  <div className="flex-grow-1">
                                    <div className="small fw-semibold">{broadcast.broadcaster.username || "N/A"}</div>
                                    <div className="text-muted small font-monospace">{broadcast.broadcaster.userId}</div>
                                  </div>
                                  {broadcast.broadcaster.userId && (
                                    <Button
                                      color="primary"
                                      size="sm"
                                      onClick={() => handleViewUserInfo(broadcast.broadcaster.userId)}
                                      className="px-2"
                                      title="View Broadcaster Details"
                                    >
                                      <i className="mdi mdi-eye"></i>
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </Col>
                          )}
                          {broadcast.moderation && (
                            <Col md={6}>
                              <div className="mb-2">
                                <label className="form-label text-muted small mb-1">
                                  <i className="mdi mdi-shield-check me-1"></i>Moderation
                                </label>
                                <div className="small">
                                  <Badge color={broadcast.moderation.moderation_pass ? "success" : "danger"} className="me-2">
                                    {broadcast.moderation.moderation_pass ? "Passed" : "Failed"}
                                  </Badge>
                                  {broadcast.moderation.moderation_decision && (
                                    <div>Decision: <strong>{broadcast.moderation.moderation_decision}</strong></div>
                                  )}
                                  {broadcast.moderation.moderation_reasons?.length > 0 && (
                                    <div className="mt-1">
                                      Reasons: {broadcast.moderation.moderation_reasons.join(", ")}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </Col>
                          )}
                        </Row>

                        {/* Tags/Categories */}
                        {(broadcast.skill?.length > 0 || broadcast.interest?.length > 0 || 
                          broadcast.sport?.length > 0 || broadcast.school?.length > 0 ||
                          broadcast.degree?.length > 0 || broadcast.job?.length > 0) && (
                          <div className="mt-2 pt-2 border-top">
                            <label className="form-label text-muted small mb-1">Categories:</label>
                            <div className="d-flex flex-wrap gap-1">
                              {broadcast.skill?.map((s, idx) => (
                                <Badge key={idx} color="secondary" className="px-2 py-1">
                                  Skill: {s.name}
                                </Badge>
                              ))}
                              {broadcast.interest?.map((i, idx) => (
                                <Badge key={idx} color="info" className="px-2 py-1">
                                  Interest: {i.name}
                                </Badge>
                              ))}
                              {broadcast.sport?.map((s, idx) => (
                                <Badge key={idx} color="warning" className="px-2 py-1">
                                  Sport: {s.name || s}
                                </Badge>
                              ))}
                              {broadcast.school?.map((s, idx) => (
                                <Badge key={idx} color="primary" className="px-2 py-1">
                                  School: {s.name || s}
                                </Badge>
                              ))}
                              {broadcast.degree?.map((d, idx) => (
                                <Badge key={idx} color="success" className="px-2 py-1">
                                  Degree: {d.name || d}
                                </Badge>
                              ))}
                              {broadcast.job?.map((j, idx) => (
                                <Badge key={idx} color="danger" className="px-2 py-1">
                                  Job: {j.name || j}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}

                        <div className="mt-2 pt-2 border-top">
                          <span className="text-muted small font-monospace">Broadcast ID: {broadcast.broadcast_id}</span>
                        </div>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              ))}
            </>
          )}

          {hasMore && (
            <div className="text-center mt-3">
              <Button
                color="primary"
                outline
                onClick={handleLoadMore}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Loading...
                  </>
                ) : (
                  <>
                    <i className="mdi mdi-arrow-down me-2"></i>
                    Load More ({totalPages - currentPage} pages remaining)
                  </>
                )}
              </Button>
            </div>
          )}

          {!hasMore && broadcasts.length > 0 && (
            <div className="text-center mt-3">
              <Badge color="secondary" className="px-3 py-2">
                All broadcasts loaded
              </Badge>
            </div>
          )}
        </ModalBody>
        <ModalFooter className="bg-light">
          <Button color="secondary" onClick={handleClose} className="px-4">
            <i className="mdi mdi-close me-2"></i>Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* Expire Broadcast Modal */}
      <Modal isOpen={expireModal} toggle={() => setExpireModal(false)} centered>
        <ModalHeader toggle={() => setExpireModal(false)} className="bg-warning text-white">
          <i className="mdi mdi-clock-alert me-2"></i>Expire Broadcast
        </ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <p className="mb-0">Are you sure you want to expire this broadcast?</p>
            {selectedBroadcast && (
              <div className="mt-2">
                <p className="mb-1"><strong>Title:</strong> {selectedBroadcast.title || "N/A"}</p>
                <p className="mb-0"><strong>Broadcast ID:</strong> {selectedBroadcast.broadcast_id}</p>
              </div>
            )}
          </div>
          <FormGroup>
            <Label>Admin Expiration Text <span className="text-danger">*</span></Label>
            <Input
              type="textarea"
              rows="4"
              value={adminExpirationText}
              onChange={(e) => setAdminExpirationText(e.target.value)}
              placeholder="Enter reason for expiration..."
              required
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => {
            setExpireModal(false);
            setAdminExpirationText("");
            setSelectedBroadcast(null);
          }} disabled={actionLoading}>
            Cancel
          </Button>
          <Button 
            color="warning" 
            onClick={handleExpireConfirm} 
            disabled={actionLoading || !adminExpirationText.trim()}
          >
            {actionLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Expiring...
              </>
            ) : (
              <>
                <i className="mdi mdi-clock-alert me-2"></i>Expire Broadcast
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>

      <UserInfoModal
        isOpen={userInfoModal}
        toggle={() => {
          setUserInfoModal(false);
          setSelectedUserId(null);
        }}
        userId={selectedUserId}
        onUpdate={() => {
          // Optionally refresh broadcasts after user action
          if (userId) {
            fetchBroadcasts(currentPage, false);
          }
        }}
      />
    </>
  );
};

export default UserBroadcastsModal;

